/**
 * Team context — a saved block managers write into EVERY AI prompt (their
 * own instructions: brand rules, risk appetite, naming conventions…). The
 * dynamic sections (stats, tags, rotation) stay system-managed. Read-only
 * without flow_library.manage; hidden entirely when empty and unmanageable.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useState, type FC } from 'react';
import { ChevronDown, ChevronRight, NotebookPen } from 'lucide-react';

import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';

import { useSaveTeamContext, useTeamContext } from '../api/hooks/useTeamContext';

const TeamContextEditor: FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('flow_library.manage');
  const stored = useTeamContext();
  const save = useSaveTeamContext();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  useEffect(() => setDraft(stored.data?.content ?? ''), [stored.data?.content]);

  const saved = stored.data?.content ?? '';
  if (!canManage && !saved) return null;
  const dirty = draft !== saved;

  return (
    <div className="rounded-md border border-border/70 px-2.5 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 text-xs font-medium"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        )}
        <NotebookPen className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        Team context
        <span className="font-normal text-muted-foreground">
          — goes into every AI prompt{saved ? ' · saved' : ' · empty'}
        </span>
      </button>
      {open ? (
        <div className="mt-2 space-y-1.5">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            readOnly={!canManage}
            maxLength={4000}
            rows={3}
            placeholder="e.g. We never eliminate before 1,000 visits. Deposits (customConversions6) matter more than registrations. Name groups in English."
            className="w-full rounded-md border border-border bg-background p-2 text-xs"
          />
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-muted-foreground">
              Stats, tags and rotation stay system-managed — this only adds
              your team&apos;s own instructions.
            </p>
            {canManage ? (
              <Button
                size="sm"
                variant="outline"
                disabled={!dirty || save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {save.isPending ? 'Saving…' : 'Save for everyone'}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TeamContextEditor;
